import { createResource, For, Match, Show, Switch } from "solid-js";
import StorageSelect from "./StorageSelect";
import SellbotOfferPicker from "./SellbotOfferPicker";
import { useApi, isOk } from "../api";
import { useInventoryApi, type StorageInfo } from "../services/inventoryApi";
import { useTranslation } from "../hooks/useTranslation";
import type { BotTaskOptionDto, BotTaskParameterDto, SellbotOfferDto } from "../api/generated/schemas";

const STORAGES = "Storages";
const SELLBOT_OFFERS = "SellbotOffers";

// Lists that belong to one bot. With several bots targeted, one bot's entry means nothing to the others.
const BOT_SCOPED_SOURCES = new Set(["SellbotZones", SELLBOT_OFFERS]);

// A page renders an input per task parameter, so every list is fetched once and shared for a short while.
const CACHE_MS = 15_000;
const cache = new Map<string, { at: number; load: Promise<unknown> }>();

function cached<T>(key: string, load: () => Promise<T>): Promise<T> {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_MS) return hit.load as Promise<T>;

    const pending = load();
    cache.set(key, { at: Date.now(), load: pending });
    pending.catch(() => cache.delete(key));
    return pending;
}

interface Props {
    botId: string;
    parameter: BotTaskParameterDto;
    value: string;
    onChange: (value: string) => void;
    // Set when the same value is sent to several bots at once.
    sharedAcrossBots?: boolean;
    // A typed value may be a template like {amount}, which a number input would refuse.
    acceptsPlaceholders?: boolean;
}

// No shared picker for task values existed: storages reuse StorageSelect, every other list a plain select.
export default function TaskParameterInput(props: Props) {
    const { t } = useTranslation();
    const api = useApi();
    const inventory = useInventoryApi();

    const source = () => {
        const optionSource = props.parameter.optionSource;
        if (!optionSource) return null;
        return props.sharedAcrossBots && BOT_SCOPED_SOURCES.has(optionSource) ? null : optionSource;
    };

    const [storages] = createResource(
        () => source() === STORAGES || null,
        () => cached<StorageInfo[]>(STORAGES, () => inventory.fetchStorages()));

    const [offers] = createResource(
        () => source() === SELLBOT_OFFERS ? props.botId : null,
        (botId) => cached<SellbotOfferDto[]>(`${botId}|${SELLBOT_OFFERS}`, async () => {
            const res = await api.sellbot.getConfig(botId);
            if (!isOk(res.data)) throw new Error(t("TASK_PARAM_LOAD_FAILED"));
            return res.data.value!.offers;
        }));

    const [options] = createResource(
        () => {
            const optionSource = source();
            return optionSource && optionSource !== STORAGES && optionSource !== SELLBOT_OFFERS ? { botId: props.botId, optionSource } : null;
        },
        ({ botId, optionSource }) => cached<BotTaskOptionDto[]>(`${botId}|${optionSource}`, async () => {
            const res = await api.botTask.getParameterOptions(botId, optionSource);
            if (!isOk(res.data)) throw new Error(t("TASK_PARAM_LOAD_FAILED"));
            return res.data.value!.options;
        }));

    // Reading a failed resource throws, so every read goes through these.
    const storageList = () => storages.state === "ready" || storages.state === "refreshing" ? storages() ?? [] : [];
    const offerList = () => offers.state === "ready" || offers.state === "refreshing" ? offers() ?? [] : [];
    const optionList = () => options.state === "ready" || options.state === "refreshing" ? options() ?? [] : [];

    const emptyLabel = () => t(props.parameter.required ? "TASK_PARAM_PICK" : "TASK_PARAM_NOT_SET");
    const unknownValue = () => props.value !== "" && !optionList().some(option => option.id === props.value);

    return (
        <Switch
            fallback={
                <input
                    type={props.parameter.type === "number" && !props.acceptsPlaceholders ? "number" : "text"}
                    step="any"
                    placeholder={props.parameter.defaultValue ?? props.parameter.type}
                    value={props.value}
                    onInput={(e) => props.onChange(e.currentTarget.value)}
                />
            }
        >
            <Match when={source() === STORAGES}>
                <StorageSelect
                    storages={storageList()}
                    value={props.value}
                    onChange={props.onChange}
                    disabled={storages.loading}
                    placeholder={storages.loading ? t("TASK_PARAM_LOADING") : emptyLabel()}
                    emptyOption={props.parameter.required ? undefined : { label: t("TASK_PARAM_NOT_SET"), icon: "fa-ban" }}
                />
            </Match>
            <Match when={source() === SELLBOT_OFFERS}>
                <SellbotOfferPicker
                    offers={offerList()}
                    value={props.value}
                    onChange={props.onChange}
                    disabled={offers.loading || offers.error != null}
                    placeholder={offers.loading
                        ? t("TASK_PARAM_LOADING")
                        : offers.error ? t("TASK_PARAM_LOAD_FAILED") : offerList().length === 0 ? t("TASK_PARAM_EMPTY") : emptyLabel()}
                />
            </Match>
            <Match when={source()}>
                <select
                    value={props.value}
                    disabled={options.loading || options.error != null}
                    onChange={(e) => props.onChange(e.currentTarget.value)}
                >
                    <Switch fallback={<option value="">{optionList().length === 0 ? t("TASK_PARAM_EMPTY") : emptyLabel()}</option>}>
                        <Match when={options.loading}><option value="">{t("TASK_PARAM_LOADING")}</option></Match>
                        <Match when={options.error}><option value="">{t("TASK_PARAM_LOAD_FAILED")}</option></Match>
                    </Switch>
                    <Show when={!options.loading && unknownValue()}>
                        <option value={props.value} selected>{t("TASK_PARAM_UNKNOWN_ENTRY")}</option>
                    </Show>
                    <For each={optionList()}>
                        {(option) => <option value={option.id} selected={option.id === props.value}>{option.name}</option>}
                    </For>
                </select>
            </Match>
        </Switch>
    );
}
